import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import MenuForm from 'components/forms/MenuForm';
import LoadingSpinner from 'components/icons/LoadingSpinner';
import { ModalDialogLink } from 'components/modal/Modal';
import { IMenu } from 'features/menu/menuAPI';
import { useUser } from 'selectors/userSelectors';
import MenuView from './MenuView';

import { useAppDispatch } from 'app/hooks';
import {
  SortableContainer,
  SortableInfo,
  SortableListInfo,
} from 'components/sortables/Sortable';
import { IMenuItem } from 'features/menu/menuItemAPI';
import { patchMenuItemAsync } from 'features/menu/menuItemSlice';
import { getFareyOrderUsingId } from 'helpers/farey-sorting';
import { AnyOrderObject, sameId } from 'helpers/objects';
import { sortByOrder } from 'helpers/sorting';
import { useCallback } from 'react';
import { useSite } from 'selectors/siteSelectors';
import './Menus.scss';

export default function Menus() {
  const { userIsAdmin } = useUser();
  const { menus } = useSite();
  const dispatch = useAppDispatch();

  const reorderMenuItems = useCallback(
    async (
      sortableInfo: SortableInfo,
      sourceItem: AnyOrderObject,
      sourceList: SortableListInfo,
      destinationItem?: AnyOrderObject,
      destinationList?: SortableListInfo,
      copy?: boolean,
    ) => {
      const data: IMenuItem = {
        ...sourceItem,
      };
      // set the new order attribute
      const before = destinationItem
        ? ['top', 'left'].includes(
            extractClosestEdge(
              sortableInfo.location.current.dropTargets[0].data,
            ) ?? '',
          )
        : false;
      data.farey_order = getFareyOrderUsingId(
        sortByOrder(
          destinationList?.items?.filter(
            (item) => !item.pinned,
          ) as AnyOrderObject[],
        ),
        sameId(sourceList, destinationList) ? sourceItem.id : -1, // -1 if moving between lists
        destinationItem ? destinationItem.id : -1,
        before,
      );
      dispatch(
        patchMenuItemAsync({
          data: { ...data, menu: destinationList },
          fromMenu: sourceList,
        }),
      ).then((response: { payload: any }) => {
        switch (response.payload?.response.status) {
          case 200:
          case 201:
            // console.log('Menu updated');
            break;
          case 500:
          default:
            // TODO: need a better action here, possibly force reload.
            console.log(
              'Menu not updated due to internal error. Please reload the page.',
            );
            break;
        }
      });
    },
    [dispatch],
  );

  return !menus ? (
    <LoadingSpinner active={true}>
      <FormattedMessage id="NAVIGATION.LOADER.MENUS" />
    </LoadingSpinner>
  ) : (
    <div className={classNames('menus_container')}>
      <div className={classNames('menus')}>
        <SortableContainer reorder={reorderMenuItems}>
          {menus?.length ? (
            menus.map((menu: IMenu, index: number) => (
              <MenuView menu={menu} key={index} />
            ))
          ) : (
            <div className="no_menus">
              <FormattedMessage id={'MENUS.NONE'} />
            </div>
          )}
          {userIsAdmin ? (
            <>
              <ModalDialogLink className="nav_item" component={MenuForm}>
                <FormattedMessage id={'MENUS.NEW_MENU'} />
              </ModalDialogLink>
            </>
          ) : null}
        </SortableContainer>
      </div>
    </div>
  );
}
